'use client';

import { useState, useEffect, Suspense } from 'react';
import { signIn, useSession } from 'next-auth/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Mail, LogIn, Sparkles, AlertTriangle, Clock, Lock } from 'lucide-react';

function LoginForm() {
  const { data: session, status } = useSession();
  const router = useRouter();
  const searchParams = useSearchParams();
  
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [codeSent, setCodeSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [countdown, setCountdown] = useState(0);

  // Redirect if already authenticated
  useEffect(() => {
    if (status === 'authenticated') {
      const callbackUrl = searchParams.get('callbackUrl') || '/';
      router.push(callbackUrl);
    }
  }, [status, router, searchParams]);

  // Handle authentication errors from NextAuth
  useEffect(() => {
    const authError = searchParams.get('error');
    if (authError) {
      const errorMessages: Record<string, string> = {
        'OAuthSignin': 'Error starting Google sign-in. Please try again.',
        'OAuthCallback': 'Error during Google authentication. Please try again.',
        'OAuthCreateAccount': 'Could not create account. Email may already be in use.',
        'EmailCreateAccount': 'Could not create account with this email.',
        'Callback': 'Authentication callback failed. Please try again.',
        'OAuthAccountNotLinked': 'This email is already registered with a different sign-in method.',
        'EmailSignin': 'Error sending verification email. Please try again.',
        'CredentialsSignin': 'Invalid login code. Please request a new one.',
        'SessionRequired': 'Please sign in to continue.',
        'Default': 'Unable to sign in. Please try again.',
      };
      
      setError(errorMessages[authError] || errorMessages['Default']);
    }
  }, [searchParams]);

  // Countdown timer for OTP expiration
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Auto-fill code from URL parameter (from email link)
  useEffect(() => {
    const urlCode = searchParams.get('code');
    const urlEmail = searchParams.get('email');
    
    if (urlCode && urlCode.length === 6 && /^\d{6}$/.test(urlCode)) {
      setCode(urlCode);
      setCodeSent(true);
      setCountdown(300); // 5 minutes
      
      // Also auto-fill email if provided
      if (urlEmail) {
        setEmail(urlEmail);
      }
    }
  }, [searchParams]);

  // Handle OTP request
  async function handleRequestCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/otp/request', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });

      if (!res.ok) {
        const data = await res.json();
        throw new Error(data.error || 'Failed to send code');
      }

      setCodeSent(true);
      setCountdown(600); // 10 minutes
      setError('');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send code');
    } finally {
      setLoading(false);
    }
  }

  // Handle OTP verification
  async function handleVerifyCode(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const result = await signIn('otp', {
        email,
        code,
        redirect: false,
      });

      if (result?.error) {
        throw new Error('Invalid or expired code');
      }

      if (result?.ok) {
        // Success - NextAuth will handle redirect
        const callbackUrl = searchParams.get('callbackUrl') || '/';
        router.push(callbackUrl);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Verification failed');
      setLoading(false);
    }
  }

  // Handle Google sign-in
  async function handleGoogleSignIn() {
    setError('');
    setLoading(true);
    try {
      const callbackUrl = searchParams.get('callbackUrl') || '/';
      await signIn('google', { callbackUrl });
    } catch (err) {
      console.error('Google sign-in error:', err);
      setError('Failed to start Google sign-in. Please try again.');
      setLoading(false);
    }
  }

  // Format countdown timer
  function formatCountdown(seconds: number) {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }

  if (status === 'loading' || status === 'authenticated') {
    return (
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      }}>
        <Sparkles size={48} color="white" style={{ animation: 'spin 2s linear infinite' }} />
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      background: 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 25%, #F8C8A0 50%, #B4A0E5 75%, #C9B6E4 100%)',
      backgroundSize: '400% 400%',
      animation: 'gradientFlow 15s ease infinite',
      padding: 'clamp(1rem, 3vw, 2rem)',
      position: 'relative',
      overflow: 'hidden',
    }}>
      {/* Animated Background Orbs */}
      <div style={{
        position: 'absolute',
        inset: 0,
        pointerEvents: 'none',
        overflow: 'hidden',
      }}>
        {/* Orb 1 */}
        <div style={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '400px',
          height: '400px',
          background: 'radial-gradient(circle, rgba(201, 182, 228, 0.6) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float1 20s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
        
        {/* Orb 2 */}
        <div style={{
          position: 'absolute',
          top: '60%',
          right: '10%',
          width: '350px',
          height: '350px',
          background: 'radial-gradient(circle, rgba(168, 216, 230, 0.5) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float2 18s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
        
        {/* Orb 3 */}
        <div style={{
          position: 'absolute',
          bottom: '5%',
          left: '50%',
          width: '300px',
          height: '300px',
          background: 'radial-gradient(circle, rgba(248, 200, 160, 0.4) 0%, transparent 70%)',
          borderRadius: '50%',
          animation: 'float3 22s ease-in-out infinite',
          filter: 'blur(60px)',
        }} />
      </div>

      {/* Floating Sparkles */}
      {[...Array(12)].map((_, i) => (
        <div
          key={i}
          style={{
            position: 'absolute',
            width: '6px',
            height: '6px',
            background: 'linear-gradient(135deg, #FFFFFF, #C9B6E4)',
            borderRadius: '50%',
            top: `${Math.random() * 100}%`,
            left: `${Math.random() * 100}%`,
            animation: `sparkleFloat ${3 + Math.random() * 4}s ease-in-out infinite`,
            animationDelay: `${Math.random() * 3}s`,
            opacity: 0.6,
            boxShadow: '0 0 10px rgba(255, 255, 255, 0.8)',
            pointerEvents: 'none',
          }}
        />
      ))}

      <div style={{
        maxWidth: '480px',
        width: '100%',
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        WebkitBackdropFilter: 'blur(20px)',
        borderRadius: '32px',
        padding: 'clamp(2rem, 5vw, 3rem) clamp(1.5rem, 4vw, 2.5rem)',
        boxShadow: '0 30px 80px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
        border: '2px solid rgba(255, 255, 255, 0.8)',
        position: 'relative',
        animation: 'slideUp 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
      }}>
        {/* Sparkle Icon Header */}
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center',
          marginBottom: '1.5rem',
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            borderRadius: '24px',
            background: 'linear-gradient(135deg, #C9B6E4, #A8D8E6)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 8px 24px rgba(201, 182, 228, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.5) inset',
            animation: 'iconFloat 3s ease-in-out infinite',
            position: 'relative',
          }}>
            <Sparkles size={40} color="white" strokeWidth={2.5} />
            
            {/* Pulsing glow */}
            <div style={{
              position: 'absolute',
              inset: '-10px',
              background: 'radial-gradient(circle, rgba(201, 182, 228, 0.4) 0%, transparent 70%)',
              borderRadius: '50%',
              animation: 'pulse 2s ease-in-out infinite',
              zIndex: -1,
            }} />
          </div>
        </div>

        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 'clamp(2rem, 4vw, 2.5rem)', animation: 'fadeIn 0.8s ease-out 0.2s backwards' }}>
          <h1 style={{
            fontSize: 'clamp(1.75rem, 5vw, 2.25rem)',
            fontWeight: 900,
            color: '#7B68EE',
            margin: '0 0 0.75rem 0',
            letterSpacing: '-0.02em',
          }}>
            Welcome to Amaya Stories
          </h1>
          <p style={{ 
            color: '#64748b', 
            fontSize: 'clamp(0.9375rem, 3vw, 1rem)', 
            margin: 0,
            fontWeight: 500,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '6px',
          }}>
            Sign in to create <span style={{ color: '#C9B6E4', fontWeight: 700 }}>magical stories</span>
            <Sparkles size={16} style={{ color: '#F8C8A0', display: 'inline-block', verticalAlign: 'middle' }} />
          </p>
        </div>

        {/* Error Message - Redesigned */}
        {error && (
          <div style={{
            background: 'linear-gradient(135deg, #FEE2E2, #FEF2F2)',
            border: '2px solid #FCA5A5',
            borderRadius: '16px',
            padding: 'clamp(0.875rem, 3vw, 1rem)',
            marginBottom: 'clamp(1.25rem, 3vw, 1.5rem)',
            color: '#991B1B',
            fontSize: 'clamp(0.875rem, 3vw, 0.9375rem)',
            fontWeight: 600,
            boxShadow: '0 4px 12px rgba(252, 165, 165, 0.2)',
            animation: 'shake 0.5s ease-in-out, fadeIn 0.3s ease-out',
            display: 'flex',
            alignItems: 'center',
            gap: '0.75rem',
          }}>
            <AlertTriangle size={20} style={{ flexShrink: 0 }} />
            <span>{error}</span>
          </div>
        )}

        {/* Google Sign-In - Redesigned */}
        <button
          onClick={handleGoogleSignIn}
          disabled={loading}
          style={{
            width: '100%',
            padding: 'clamp(1rem, 3vw, 1.125rem)',
            border: '2px solid rgba(226, 232, 240, 0.8)',
            borderRadius: '16px',
            background: 'linear-gradient(135deg, #FFFFFF, #F8FAFC)',
            color: '#334155',
            fontSize: 'clamp(0.9375rem, 3vw, 1rem)',
            fontWeight: 700,
            cursor: loading ? 'not-allowed' : 'pointer',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 'clamp(0.75rem, 2vw, 1rem)',
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
            marginBottom: 'clamp(1.5rem, 3vw, 2rem)',
            opacity: loading ? 0.6 : 1,
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
            position: 'relative',
            overflow: 'hidden',
            animation: 'fadeIn 0.8s ease-out 0.4s backwards',
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.borderColor = '#C9B6E4';
              e.currentTarget.style.background = 'linear-gradient(135deg, #FAFBFF, #F0F3FF)';
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 8px 24px rgba(201, 182, 228, 0.25)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = 'rgba(226, 232, 240, 0.8)';
            e.currentTarget.style.background = 'linear-gradient(135deg, #FFFFFF, #F8FAFC)';
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 12px rgba(0, 0, 0, 0.05)';
          }}
        >
          <svg width="20" height="20" viewBox="0 0 20 20">
            <path fill="#4285F4" d="M19.6 10.23c0-.82-.1-1.42-.25-2.05H10v3.72h5.5c-.15.96-.74 2.31-2.04 3.22v2.45h3.16c1.89-1.73 2.98-4.3 2.98-7.34z"/>
            <path fill="#34A853" d="M13.46 15.13c-.83.59-1.96 1-3.46 1-2.64 0-4.88-1.74-5.68-4.15H1.07v2.52C2.72 17.75 6.09 20 10 20c2.7 0 4.96-.89 6.62-2.42l-3.16-2.45z"/>
            <path fill="#FBBC05" d="M3.99 10c0-.69.12-1.35.32-1.97V5.51H1.07A9.973 9.973 0 000 10c0 1.61.39 3.14 1.07 4.49l3.24-2.52c-.2-.62-.32-1.28-.32-1.97z"/>
            <path fill="#EA4335" d="M10 3.88c1.88 0 3.13.81 3.85 1.48l2.84-2.76C14.96.99 12.7 0 10 0 6.09 0 2.72 2.25 1.07 5.51l3.24 2.52C5.12 5.62 7.36 3.88 10 3.88z"/>
          </svg>
          Continue with Google
        </button>

        {/* Divider - Redesigned */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'clamp(1rem, 3vw, 1.25rem)',
          margin: 'clamp(1.5rem, 4vw, 2rem) 0',
          animation: 'fadeIn 0.8s ease-out 0.6s backwards',
        }}>
          <div style={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(90deg, transparent, #E2E8F0, transparent)',
          }}></div>
          <span style={{ 
            color: '#94A3B8', 
            fontSize: 'clamp(0.8125rem, 2.5vw, 0.875rem)', 
            fontWeight: 700,
            padding: '0.25rem 0.75rem',
            background: 'linear-gradient(135deg, #F1F5F9, #E2E8F0)',
            borderRadius: '20px',
            border: '1px solid #E2E8F0',
          }}>
            OR
          </span>
          <div style={{ 
            flex: 1, 
            height: '2px', 
            background: 'linear-gradient(90deg, transparent, #E2E8F0, transparent)',
          }}></div>
        </div>

        {/* Email OTP Form */}
        {!codeSent ? (
          <form onSubmit={handleRequestCode} style={{ animation: 'fadeIn 0.8s ease-out 0.8s backwards' }}>
            <label style={{
              display: 'block',
              color: '#334155',
              fontSize: 'clamp(0.875rem, 3vw, 0.9375rem)',
              fontWeight: 700,
              marginBottom: 'clamp(0.5rem, 2vw, 0.625rem)',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <Mail size={18} style={{ color: '#C9B6E4' }} />
              Email Address
            </label>
            <div style={{ position: 'relative' }}>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                disabled={loading}
                style={{
                  width: '100%',
                  padding: 'clamp(1rem, 3vw, 1.125rem) clamp(1rem, 3vw, 1.25rem)',
                  border: '2px solid #E2E8F0',
                  borderRadius: '16px',
                  fontSize: 'clamp(0.9375rem, 3vw, 1rem)',
                  marginBottom: 'clamp(1rem, 3vw, 1.25rem)',
                  boxSizing: 'border-box',
                  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                  background: '#FFFFFF',
                  boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                }}
                onFocus={(e) => {
                  e.target.style.borderColor = '#C9B6E4';
                  e.target.style.boxShadow = '0 4px 16px rgba(201, 182, 228, 0.2)';
                  e.target.style.transform = 'translateY(-2px)';
                }}
                onBlur={(e) => {
                  e.target.style.borderColor = '#E2E8F0';
                  e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                  e.target.style.transform = 'translateY(0)';
                }}
              />
            </div>
            <button
              type="submit"
              disabled={loading || !email}
              style={{
                width: '100%',
                padding: 'clamp(1rem, 3vw, 1.125rem)',
                background: loading || !email 
                  ? 'linear-gradient(135deg, #CBD5E1, #94A3B8)' 
                  : 'linear-gradient(135deg, #C9B6E4 0%, #A8D8E6 50%, #B4A0E5 100%)',
                backgroundSize: '200% auto',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: 'clamp(0.9375rem, 3vw, 1rem)',
                fontWeight: 700,
                cursor: loading || !email ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(0.75rem, 2vw, 1rem)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: loading || !email ? 0.6 : 1,
                boxShadow: loading || !email ? 'none' : '0 4px 16px rgba(201, 182, 228, 0.3)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!loading && email) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(201, 182, 228, 0.5)';
                  e.currentTarget.style.backgroundPosition = 'right center';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(201, 182, 228, 0.3)';
                e.currentTarget.style.backgroundPosition = 'left center';
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  Sending...
                </>
              ) : (
                <>
                  <Mail size={20} strokeWidth={2.5} />
                  Send Login Code
                </>
              )}
            </button>
          </form>
        ) : (
          <form onSubmit={handleVerifyCode} style={{ animation: 'fadeIn 0.8s ease-out 0.8s backwards' }}>
            <div style={{
              background: 'linear-gradient(135deg, #EFF6FF, #DBEAFE)',
              border: '2px solid #93C5FD',
              borderRadius: '18px',
              padding: 'clamp(1rem, 3vw, 1.25rem)',
              marginBottom: 'clamp(1.25rem, 3vw, 1.5rem)',
              fontSize: 'clamp(0.875rem, 3vw, 0.9375rem)',
              color: '#1E3A8A',
              boxShadow: '0 4px 12px rgba(147, 197, 253, 0.2)',
              animation: 'pulse 2s ease-in-out infinite',
            }}>
              <div style={{ 
                fontWeight: 700, 
                marginBottom: '0.5rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
              }}>
                <Mail size={22} style={{ color: '#3B82F6', flexShrink: 0 }} />
                <span>Code sent to {email}</span>
              </div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                fontSize: 'clamp(0.8125rem, 2.5vw, 0.875rem)',
                color: '#2563EB',
              }}>
                <Clock size={18} style={{ flexShrink: 0 }} />
                <span>Expires in <strong>{formatCountdown(countdown)}</strong></span>
              </div>
            </div>

            <label style={{
              display: 'block',
              color: '#334155',
              fontSize: 'clamp(0.875rem, 3vw, 0.9375rem)',
              fontWeight: 700,
              marginBottom: 'clamp(0.5rem, 2vw, 0.625rem)',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
            }}>
              <Lock size={18} style={{ color: '#B4A0E5' }} />
              Enter 6-Digit Code
            </label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              placeholder="••••••"
              required
              disabled={loading}
              autoComplete="one-time-code"
              inputMode="numeric"
              maxLength={6}
              style={{
                width: '100%',
                padding: 'clamp(1rem, 3vw, 1.25rem)',
                border: '2px solid #E2E8F0',
                borderRadius: '18px',
                fontSize: 'clamp(1.5rem, 5vw, 2rem)',
                fontWeight: 800,
                letterSpacing: 'clamp(0.375rem, 2vw, 0.625rem)',
                textAlign: 'center',
                marginBottom: 'clamp(1rem, 3vw, 1.25rem)',
                boxSizing: 'border-box',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                background: 'linear-gradient(135deg, #FFFFFF, #F8FAFC)',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.04)',
                color: '#1E293B',
              }}
              onFocus={(e) => {
                e.target.style.borderColor = '#A8D8E6';
                e.target.style.boxShadow = '0 6px 24px rgba(168, 216, 230, 0.3)';
                e.target.style.transform = 'scale(1.02)';
              }}
              onBlur={(e) => {
                e.target.style.borderColor = '#E2E8F0';
                e.target.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.04)';
                e.target.style.transform = 'scale(1)';
              }}
            />
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              style={{
                width: '100%',
                padding: 'clamp(1rem, 3vw, 1.125rem)',
                background: loading || code.length !== 6
                  ? 'linear-gradient(135deg, #CBD5E1, #94A3B8)'
                  : 'linear-gradient(135deg, #B4A0E5 0%, #C9B6E4 50%, #A8D8E6 100%)',
                backgroundSize: '200% auto',
                color: 'white',
                border: 'none',
                borderRadius: '16px',
                fontSize: 'clamp(0.9375rem, 3vw, 1rem)',
                fontWeight: 700,
                cursor: loading || code.length !== 6 ? 'not-allowed' : 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 'clamp(0.75rem, 2vw, 1rem)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                opacity: loading || code.length !== 6 ? 0.6 : 1,
                boxShadow: loading || code.length !== 6 ? 'none' : '0 4px 16px rgba(180, 160, 229, 0.4)',
                marginBottom: 'clamp(0.75rem, 2vw, 1rem)',
                position: 'relative',
                overflow: 'hidden',
              }}
              onMouseEnter={(e) => {
                if (!loading && code.length === 6) {
                  e.currentTarget.style.transform = 'translateY(-3px)';
                  e.currentTarget.style.boxShadow = '0 8px 32px rgba(180, 160, 229, 0.6)';
                  e.currentTarget.style.backgroundPosition = 'right center';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = '0 4px 16px rgba(180, 160, 229, 0.4)';
                e.currentTarget.style.backgroundPosition = 'left center';
              }}
            >
              {loading ? (
                <>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    border: '3px solid rgba(255, 255, 255, 0.3)',
                    borderTopColor: 'white',
                    borderRadius: '50%',
                    animation: 'spin 0.8s linear infinite',
                  }} />
                  Verifying...
                </>
              ) : (
                <>
                  <LogIn size={20} strokeWidth={2.5} />
                  Sign In
                </>
              )}
            </button>
            <button
              type="button"
              onClick={() => {
                setCodeSent(false);
                setCode('');
                setError('');
                setCountdown(0);
              }}
              disabled={loading}
              style={{
                width: '100%',
                padding: 'clamp(0.75rem, 2vw, 0.875rem)',
                background: 'transparent',
                color: '#94A3B8',
                border: '2px solid #E2E8F0',
                borderRadius: '14px',
                fontSize: 'clamp(0.875rem, 2.5vw, 0.9375rem)',
                fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer',
                transition: 'all 0.2s ease',
              }}
              onMouseEnter={(e) => {
                if (!loading) {
                  e.currentTarget.style.borderColor = '#C9B6E4';
                  e.currentTarget.style.color = '#C9B6E4';
                  e.currentTarget.style.background = 'rgba(201, 182, 228, 0.05)';
                }
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.borderColor = '#E2E8F0';
                e.currentTarget.style.color = '#94A3B8';
                e.currentTarget.style.background = 'transparent';
              }}
            >
              ← Back to email
            </button>
          </form>
        )}
      </div>

      <style jsx>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        @keyframes gradientFlow {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        
        @keyframes float1 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(30px, -30px) scale(1.1); }
          66% { transform: translate(-20px, 20px) scale(0.9); }
        }
        
        @keyframes float2 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          33% { transform: translate(-25px, 25px) scale(1.05); }
          66% { transform: translate(20px, -15px) scale(0.95); }
        }
        
        @keyframes float3 {
          0%, 100% { transform: translate(0, 0) scale(1); }
          50% { transform: translate(15px, -20px) scale(1.08); }
        }
        
        @keyframes sparkleFloat {
          0%, 100% {
            transform: translateY(0) scale(1);
            opacity: 0.6;
          }
          50% {
            transform: translateY(-20px) scale(1.2);
            opacity: 1;
          }
        }
        
        @keyframes slideUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
        
        @keyframes pulse {
          0%, 100% {
            opacity: 0.6;
            transform: scale(1);
          }
          50% {
            opacity: 1;
            transform: scale(1.05);
          }
        }
        
        @keyframes iconFloat {
          0%, 100% {
            transform: translateY(0);
          }
          50% {
            transform: translateY(-10px);
          }
        }
        
        @keyframes shimmer {
          0% { background-position: 0% center; }
          100% { background-position: 200% center; }
        }
        
        @keyframes shake {
          0%, 100% { transform: translateX(0); }
          10%, 30%, 50%, 70%, 90% { transform: translateX(-5px); }
          20%, 40%, 60%, 80% { transform: translateX(5px); }
        }
        
        /* Responsive adjustments */
        @media (max-width: 640px) {
          .sparkle-float {
            display: none;
          }
        }
      `}</style>
    </div>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={
      <div style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(180deg, #f0f4ff 0%, #fef1f7 100%)'
      }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{
            width: '48px',
            height: '48px',
            border: '4px solid #667eea',
            borderTopColor: 'transparent',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto'
          }} />
        </div>
      </div>
    }>
      <LoginForm />
    </Suspense>
  );
}

